import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, ScrollText, Settings, LogOut, Menu, X, 
  ChevronDown, Bell, Search, Building2, ChevronLeft, FileText,
  UserCircle, Users, Briefcase, CalendarClock, DollarSign, BarChart3, Handshake, Mail
} from 'lucide-react';

const adminNavItems = [
  { icon: LayoutDashboard, label: 'Admin Dashboard', path: '/crm/admin' },
  { icon: ScrollText, label: 'Contract Documents', path: '/crm/admin/contracts' },
  { icon: CalendarClock, label: 'Booking Settings', path: '/crm/admin/booking-settings' },
  { icon: DollarSign, label: 'Monthly System Costs', path: '/crm/admin/system-costs' },
  { icon: BarChart3, label: 'Accounting', path: '/crm/admin/accounting' },
  { icon: Mail, label: 'Email Stats', path: '/crm/admin/email-stats' },
  { icon: Handshake, label: 'Partners', path: '/crm/admin/partners', partnerOnly: true },
  { icon: FileText, label: 'Quote Templates', path: '/crm/admin/quote-templates', future: true },
  { icon: Users, label: 'Team Management', path: '/crm/admin/team', future: true },
  { icon: Briefcase, label: 'Service Catalog', path: '/crm/admin/services', future: true },
];

export const AdminLayout = ({ children }) => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/crm/login');
  };

  const isActive = (path) => {
    if (path === '/crm/admin') return location.pathname === '/crm/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Admin Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-[#1E3A5F] to-[#0F2744] transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } flex flex-col`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/crm/admin" className="flex items-center gap-3">
            {sidebarOpen ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-lg flex items-center justify-center">
                  <Settings className="w-4 h-4 text-white" />
                </div>
                <span className="text-white font-semibold">Admin Panel</span>
              </div>
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-xl flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5 text-white" />
              </div>
            )}
          </Link>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white p-1"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Back to CRM Link */}
        <div className="px-3 pt-4 pb-2">
          <Link
            to="/crm"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-400 hover:bg-white/5 hover:text-white transition-all duration-200 ${
              sidebarOpen ? '' : 'justify-center'
            }`}
          >
            <ChevronLeft className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">Back to CRM</span>}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 px-3 space-y-1 overflow-y-auto">
          {sidebarOpen && (
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Administration
            </div>
          )}
          {adminNavItems.map((item) => {
            const isCurrent = isActive(item.path);
            // Partner menu only visible to Danny and Mel
            if (item.partnerOnly) {
              const email = (user?.email || '').toLowerCase();
              if (email !== 'mel@a2gdesigns.com' && email !== 'dt@dmerus.com') return null;
            }
            return (
              <Link
                key={item.path}
                to={item.future ? '#' : item.path}
                onClick={(e) => item.future && e.preventDefault()}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                  item.future 
                    ? 'text-gray-500 cursor-not-allowed opacity-50'
                    : isCurrent
                      ? 'bg-[#84CC16] text-white'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                } ${sidebarOpen ? '' : 'justify-center'}`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${
                  item.future ? 'text-gray-500' : isCurrent ? 'text-white' : 'text-gray-400 group-hover:text-[#84CC16]'
                }`} />
                {sidebarOpen && (
                  <div className="flex items-center justify-between flex-1">
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.future && (
                      <span className="text-[10px] bg-gray-600 text-gray-300 px-1.5 py-0.5 rounded-full">Soon</span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-white/10">
          <Link
            to="/crm/profile"
            className={`flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors ${sidebarOpen ? '' : 'justify-center'}`}
          >
            <div className="w-9 h-9 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-sm">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-400 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            )}
          </Link>
          <button
            onClick={handleLogout}
            className={`mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors ${
              sidebarOpen ? '' : 'justify-center'
            }`}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Link to="/crm" className="hover:text-[#014DB7]">CRM</Link>
              <span>/</span>
              <span className="text-[#0A1628] font-medium">Admin</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            <div className="relative">
              <button 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-[#84CC16] to-[#65a30d] rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="font-medium text-gray-900">{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                  <Link
                    to="/crm/profile"
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <UserCircle className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  <Link
                    to="/crm"
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    <span>Back to CRM</span>
                  </Link>
                  {isSuperAdmin && (
                    <Link
                      to="/crm/super-admin"
                      className="flex items-center gap-2 px-4 py-2 text-[#014DB7] hover:bg-[#014DB7]/5 font-medium"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Super Admin Panel</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
